import React from 'react';
import classNames from 'classnames';
import Dialog from '@mui/material/Dialog';
import { GetLendingSupportDealFinanceOfferResponse } from '../../hooks/services';
import { formatCurrency } from '../../util/format';
import { CopyableCode } from './CopyableCode';
import './ApplicationOverview.scss';

export interface FinanceApplicationOverviewData {
  carPrice?: number;
  downPayment: number;
  financeAmount: number;
  months: number;
}

type FinanceResponse =
  GetLendingSupportDealFinanceOfferResponse[number]['financeResponses'][number];

export interface ApplicationOverviewDialogData {
  requestedTerms: FinanceApplicationOverviewData;
  responses: FinanceResponse[];
}

interface Props {
  open: boolean;
  data: ApplicationOverviewDialogData | null;
  onClose: () => void;
}

const EMPTY = '—';

const COLUMNS = [
  'Response',
  'Status',
  'Badge',
  'Finance Amount',
  'Down Payment',
  'Term',
  'Monthly Payment',
  'APR',
  'Asset Value',
  'Fees',
  'Tax Amount',
];

const shortId = (id: string) => id.slice(0, 8);

function ResponseRow({ response }: { response: FinanceResponse }) {
  const terms = response.initialSubmissionData;
  return (
    <tr
      className={classNames({
        'application-overview-row-selected': response.isSelected,
      })}
      data-testid={`application-overview-response-${response.id}`}
    >
      <td>
        <CopyableCode value={response.id} title={response.id}>
          {shortId(response.id)}
        </CopyableCode>
        {response.isSelected && ' (selected)'}
      </td>
      <td>{response.status || EMPTY}</td>
      <td>
        {terms.primaryBadgeValue ? (
          <span className="lending-support-code">
            {terms.primaryBadgeValue}
          </span>
        ) : (
          EMPTY
        )}
      </td>
      <td>{formatCurrency(terms.financeAmount)}</td>
      <td>{formatCurrency(terms.downPayment)}</td>
      <td>{terms.months} months</td>
      <td>{formatCurrency(terms.monthly)}</td>
      <td>{(terms.apr * 100).toFixed(2)}%</td>
      <td>{formatCurrency(terms.assetValue)}</td>
      <td>{formatCurrency(terms.fees)}</td>
      <td>{formatCurrency(terms.taxAmount)}</td>
    </tr>
  );
}

function RequestedTerms({ terms }: { terms: FinanceApplicationOverviewData }) {
  const items = [
    { label: 'Finance Amount', value: formatCurrency(terms.financeAmount) },
    { label: 'Down Payment', value: formatCurrency(terms.downPayment) },
    { label: 'Term', value: `${terms.months} months` },
    ...(terms.carPrice !== undefined
      ? [{ label: 'Vehicle Price', value: formatCurrency(terms.carPrice) }]
      : []),
  ];
  return (
    <div
      className="application-overview-requested"
      data-testid="application-overview-requested"
    >
      <div className="application-overview-section">Requested Terms</div>
      <dl className="application-overview-terms">
        {items.map(({ label, value }) => (
          <div key={label} className="application-overview-term">
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * Shows the requested terms above a table of every response on the
 * application, one row each. The dealer-selected response, if any, is marked.
 */
export function ApplicationOverviewDialog({ open, data, onClose }: Props) {
  if (!data) {
    return null;
  }

  const { requestedTerms, responses } = data;

  return (
    <Dialog onClose={onClose} open={open} maxWidth="lg">
      <div className="application-overview-dialog">
        <div className="application-overview-title">Application Overview</div>
        <RequestedTerms terms={requestedTerms} />
        <div className="application-overview-section">Responses</div>
        {responses.length === 0 ? (
          <div className="application-overview-empty">No responses</div>
        ) : (
          <div className="application-overview-scroll">
            <table className="application-overview-table">
              <thead>
                <tr>
                  {COLUMNS.map((column) => (
                    <th key={column}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {responses.map((response) => (
                  <ResponseRow key={response.id} response={response} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Dialog>
  );
}
